import { useEffect, useRef } from 'react'
import { CartItemGroup } from '@open-tender/types'
import { formatDollars } from '@open-tender/utils'
import { Body, Checkmark, Heading } from 'components'
import { getSelectedOption } from 'utils/assortment'
import {
  AssortmentBack,
  AssortmentChoice,
  AssortmentChoiceMeta,
  AssortmentChoiceText,
  AssortmentOverlay,
  AssortmentSheetHeader,
  AssortmentSheetList,
  AssortmentSheetView,
} from './MenuItemAssortment.styled'

const AssortmentSheet = ({
  group,
  onSelect,
  onClose,
}: {
  group: CartItemGroup
  onSelect: (optionId: number) => void
  onClose: () => void
}) => {
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const selected = getSelectedOption(group)
  const titleId = `assortment-sheet-title-${group.id}`

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    sheetRef.current?.focus()

    // Captured so Escape closes the sheet instead of opening the cart.
    const onKeyDown = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') {
        evt.stopPropagation()
        onCloseRef.current()
        return
      }
      if (evt.key !== 'Tab' || !sheetRef.current) return
      const focusable = sheetRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled])'
      )
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault()
        last.focus()
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      previous?.focus?.()
    }
  }, [])

  return (
    <>
      <AssortmentOverlay onClick={onClose} />
      <AssortmentSheetView
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <AssortmentSheetHeader>
          <Heading as="h2" size="big" id={titleId}>
            Choose for {group.name}
          </Heading>
          <AssortmentBack type="button" aria-label="Close" onClick={onClose}>
            &times;
          </AssortmentBack>
        </AssortmentSheetHeader>
        <AssortmentSheetList role="radiogroup" aria-labelledby={titleId}>
          {group.options.map((option) => {
            const isSelected = option.id === selected?.id
            const price = Number(option.price) || 0
            return (
              <li key={option.id}>
                <AssortmentChoice
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  selected={isSelected}
                  disabled={option.isSoldOut}
                  onClick={() => onSelect(option.id)}
                >
                  <AssortmentChoiceText>
                    <Heading size="main">{option.name}</Heading>
                    {option.isSoldOut ? (
                      <Body size="small" as="div">
                        Out of stock
                      </Body>
                    ) : null}
                  </AssortmentChoiceText>
                  <AssortmentChoiceMeta>
                    {price > 0 ? (
                      <Body size="small">+{formatDollars(`${price}`)}</Body>
                    ) : null}
                    {isSelected ? <Checkmark /> : null}
                  </AssortmentChoiceMeta>
                </AssortmentChoice>
              </li>
            )
          })}
        </AssortmentSheetList>
      </AssortmentSheetView>
    </>
  )
}

export default AssortmentSheet
